<script setup>
import { ref, computed, onBeforeMount } from "vue";
import { useCompanyStore } from "@/stores/company";
import { useUiStore } from "@/stores/ui";
import { useThemeAssignmentsStore } from "@/stores/themeAssignments";

const uiStore = useUiStore();
const themeAssignmentsStore = useThemeAssignmentsStore();
const emit = defineEmits(["refresh"]);
const companyId = useCompanyStore().companyId;
let id = ref(null);
let stores = ref([]);
let storesOptions = ref([]);
let isStoresLoading = ref(false);
let areas = ref([]);
let areasOptions = ref([]);
let isAreasLoading = ref(false);
let devices = ref([]);
let devicesOptions = ref([]);
let isDevicesLoading = ref(false);

let priority = ref(null);
let scheduleFrom = ref(null);
let scheduleTo = ref(null);
let scheduleType = ref({ name: "always", description: "Always" });
const scheduleTypeOptions = ref([
  { name: "always", description: "Always" },
  { name: "weekly", description: "Weekly" },
  { name: "monthly", description: "Monthly" },
  { name: "yearly", description: "Yearly" },
]);

let daysOfWeek = ref([]);
let daysOfMonth = ref([]);
let monthsOfYear = ref([]);

let startTime = ref(null);
let endTime = ref(null);

const daysOfWeekOptions = ref([
  { id: 1, name: "Monday" },
  { id: 2, name: "Tuesday" },
  { id: 3, name: "Wednesday" },
  { id: 4, name: "Thursday" },
  { id: 5, name: "Friday" },
  { id: 6, name: "Saturday" },
  { id: 7, name: "Sunday" },
]);
const daysOfMonthOptions = ref([
  { id: 1, name: "1" },
  { id: 2, name: "2" },
  { id: 3, name: "3" },
  { id: 4, name: "4" },
  { id: 5, name: "5" },
  { id: 6, name: "6" },
  { id: 7, name: "7" },
  { id: 8, name: "8" },
  { id: 9, name: "9" },
  { id: 10, name: "10" },
  { id: 11, name: "11" },
  { id: 12, name: "12" },
  { id: 13, name: "13" },
  { id: 14, name: "14" },
  { id: 15, name: "15" },
  { id: 16, name: "16" },
  { id: 17, name: "17" },
  { id: 18, name: "18" },
  { id: 19, name: "19" },
  { id: 20, name: "20" },
  { id: 21, name: "21" },
  { id: 22, name: "22" },
  { id: 23, name: "23" },
  { id: 24, name: "24" },
  { id: 25, name: "25" },
  { id: 26, name: "26" },
  { id: 27, name: "27" },
  { id: 28, name: "28" },
  { id: 29, name: "29" },
  { id: 30, name: "30" },
  { id: 31, name: "31" },
]);
const monthsOfYearOptions = ref([
  { id: 1, name: "January" },
  { id: 2, name: "February" },
  { id: 3, name: "March" },
  { id: 4, name: "April" },
  { id: 5, name: "May" },
  { id: 6, name: "June" },
  { id: 7, name: "July" },
  { id: 8, name: "August" },
  { id: 9, name: "September" },
  { id: 10, name: "October" },
  { id: 11, name: "November" },
  { id: 12, name: "December" },
]);

const getModalInfo = computed(() => uiStore.modalData);
const getModalAction = computed(() => uiStore.modalAction);

let isSubmitted = ref(false);

const resetStoreData = () => {
  uiStore.resetModalStore();
};

const disabledDates = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day
  return (date) => {
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0); // Reset time to start of day
    return compareDate < today;
  };
});

const disabledScheduleToDates = computed(() => {
  if (!scheduleFrom.value) return;
  const today = new Date(scheduleFrom.value);
  // today.setHours(0, 0, 0, 0); // Reset time to start of day
  return (date) => {
    const compareDate = new Date(date);
    // compareDate.setHours(0, 0, 0, 0); // Reset time to start of day
    return compareDate <= today;
  };
});

const onScheduleTypeChange = () => {
  daysOfWeek.value = [];
  daysOfMonth.value = [];
  monthsOfYear.value = [];
  startTime.value = null;
  endTime.value = null;
};

const searchByName = (val, type) => {
  if (val && val.length >= 3) {
    if (type === "store") {
      fetchStoresByName(val);
    } else if (type === "area") {
      fetchAreasByName(val);
    } else if (type === "device") {
      fetchDevicesByName(val);
    }
  }
};

const fetchStoresByName = (text) => {
  isStoresLoading.value = true;
  storesOptions.value = [];
  themeAssignmentsStore
    .fetchSitesBySearch({ companyId, params: { search: text } })
    .then((res) => {
      isStoresLoading.value = false;
      storesOptions.value = res.data;
    })
    .catch(() => {});
};

const fetchAreasByName = (text) => {
  isAreasLoading.value = true;
  areasOptions.value = [];
  themeAssignmentsStore
    .fetchAreasBySearch({ companyId, params: { search: text } })
    .then((res) => {
      isAreasLoading.value = false;
      areasOptions.value = res.data;
    })
    .catch(() => {});
};

const fetchDevicesByName = (text) => {
  isDevicesLoading.value = true;
  devicesOptions.value = [];
  themeAssignmentsStore
    .fetchDevicesBySearch({ companyId, params: { search: text } })
    .then((res) => {
      isDevicesLoading.value = false;
      devicesOptions.value = res.data;
    })
    .catch(() => {});
};

const changeScheduleFromDates = () => {
  scheduleTo.value = null;
};

const formatTime = (time) => {
  if (!time) return null;
  if (typeof time === "string") return time;
  if (time.hours !== undefined) {
    const hours = String(time.hours).padStart(2, "0");
    const minutes = String(time.minutes).padStart(2, "0");
    const seconds = String(time.seconds || 0).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }
  return null;
};

const onSubmit = () => {
  isSubmitted.value = true;
  let data = {
    company_id: companyId,
    data: {
      theme_id: id.value,
      priority: priority.value,
      schedule_from: scheduleFrom.value,
      schedule_to: scheduleTo.value,
      schedule_type: scheduleType.value ? scheduleType.value.name : null,
      sites: stores.value.map((store) => store.id),
      areas: areas.value.map((area) => area.id),
      devices: devices.value.map((device) => device.id),
      schedule_config: {
        start_time: formatTime(startTime.value),
        end_time: formatTime(endTime.value),
        days_of_week: daysOfWeek.value
          ? daysOfWeek.value.map((day) => day.id)
          : null,
        days_of_month: daysOfMonth.value
          ? daysOfMonth.value.map((day) => day.id)
          : null,
        months_of_year: monthsOfYear.value
          ? monthsOfYear.value.map((month) => month.id)
          : null,
      },
    },
  };

  themeAssignmentsStore
    .assignTheme(data)
    .then(() => {
      isSubmitted.value = false;
      resetStoreData();
      emit("refresh");
    })
    .catch(() => {
      isSubmitted.value = false;
    });
};

onBeforeMount(() => {
  if (getModalAction.value == "assign_theme") {
    id.value = getModalInfo.value.id;
  }
});
</script>

<template>
  <DModal v-if="getModalAction === 'assign_theme'" :visible="uiStore.modalState" @update:visible="val => { if (!val) resetStoreData() }" :main_classes="[]" :dialog_classes="[]">
    <template #title> Apply Theme </template>

    <template #body>
      <div class="relative mb-3">
        <span v-if="stores.length" class="multiselect-title-span"
          >Stores (Optional)</span
        >
        <VMultiselect
          :class="{ 'multiselect-options': stores.length }"
          v-model="stores"
          :options="storesOptions"
          :disabled="false"
          :multiple="true"
          :searchable="true"
          :show-labels="false"
          placeholder="Stores (Optional)"
          trackBy="id"
          label="name"
          :loading="isStoresLoading"
          open-direction="bottom"
          :internal-search="false"
          :clear-on-select="false"
          :close-on-select="false"
          :show-no-results="true"
          :hide-selected="false"
          @search-change="searchByName($event, 'store')"
          selectLabel="Press enter to select"
          deselectLabel="Press enter to remove"
        >
          <template v-slot:noOptions>
            <span>Search by site name</span>
          </template>
        </VMultiselect>
      </div>

      <div class="relative mb-3">
        <span v-if="areas.length" class="multiselect-title-span"
          >Areas (Optional)</span
        >
        <VMultiselect
          :class="{ 'multiselect-options': areas.length }"
          v-model="areas"
          :options="areasOptions"
          :disabled="false"
          :multiple="true"
          :searchable="true"
          :show-labels="false"
          placeholder="Areas (Optional)"
          trackBy="id"
          label="name"
          :loading="isAreasLoading"
          open-direction="bottom"
          :internal-search="false"
          :clear-on-select="false"
          :close-on-select="false"
          :show-no-results="true"
          :hide-selected="false"
          @search-change="searchByName($event, 'area')"
          selectLabel="Press enter to select"
          deselectLabel="Press enter to remove"
        >
          <template v-slot:noOptions>
            <span>Search by area name</span>
          </template>
        </VMultiselect>
      </div>

      <div class="relative mb-3">
        <span v-if="devices.length" class="multiselect-title-span"
          >Devices (Optional)</span
        >
        <VMultiselect
          :class="{ 'multiselect-options': devices.length }"
          v-model="devices"
          :options="devicesOptions"
          :disabled="false"
          :multiple="true"
          :searchable="true"
          :show-labels="false"
          placeholder="Devices (Optional)"
          trackBy="id"
          label="name"
          :loading="isDevicesLoading"
          open-direction="bottom"
          :internal-search="false"
          :clear-on-select="false"
          :close-on-select="false"
          :show-no-results="true"
          :hide-selected="false"
          @search-change="searchByName($event, 'device')"
          selectLabel="Press enter to select"
          deselectLabel="Press enter to remove"
        >
          <template v-slot:noOptions>
            <span>Search by device name</span>
          </template>
        </VMultiselect>
      </div>

      <DInput
        :value="priority != null ? String(priority) : ''"
        @input="(e) => { priority = e.target.value ? parseInt(e.target.value) : null; }"
        label="Priority (Optional)"
        width="100%"
        type="number"
        class="mb-3"
      />

                  <div class="setting-seperator text-center"></div>

      <div class="flex items-center justify-between mb-3">
        <div class="flex">
          <div class="relative">
            <span v-if="scheduleFrom" class="datepicker-title-span"
              >Schedule From</span
            >
            <VueDatePicker
              id="schedule-from-picker"
              auto-apply
              :clearable="scheduleFrom ? true : false"
              format="dd/MM/yyyy"
              v-model="scheduleFrom"
              :month-change-on-scroll="false"
              type="date"
              :disabled-dates="disabledDates"
              @update:model-value="changeScheduleFromDates"
              @time-picker-change="changeScheduleFromDates"
              @cleared="changeScheduleFromDates"
              @closed="changeScheduleFromDates"
              mode="date"
              :enable-time-picker="false"
            >
              <template #dp-input="{ value }">
                <input
                  placeholder="Schedule From"
                  type="text"
                  :value="value"
                  class="custom-date-picker"
                  :class="scheduleFrom ? 'pt-15' : ''"
                />
              </template>

              <template v-if="!scheduleFrom" #input-icon>
                <i class="input-slot-image">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      :fill="scheduleFrom ? '#2D8425' : '#787878'"
                      d="M0.666626 14.8327C0.666626 16.2493 1.74996 17.3327 3.16663 17.3327H14.8333C16.25 17.3327 17.3333 16.2493 17.3333 14.8327V8.16602H0.666626V14.8327ZM14.8333 2.33268H13.1666V1.49935C13.1666 0.999349 12.8333 0.666016 12.3333 0.666016C11.8333 0.666016 11.5 0.999349 11.5 1.49935V2.33268H6.49996V1.49935C6.49996 0.999349 6.16663 0.666016 5.66663 0.666016C5.16663 0.666016 4.83329 0.999349 4.83329 1.49935V2.33268H3.16663C1.74996 2.33268 0.666626 3.41602 0.666626 4.83268V6.49935H17.3333V4.83268C17.3333 3.41602 16.25 2.33268 14.8333 2.33268Z"
                    />
                  </svg>
                </i>
              </template>
              <template v-if="scheduleFrom" #clear-icon="{ clear }">
                <span @click="clear">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      fill="#F46A6A"
                      d="M11 0C4.91333 0 0 4.91333 0 11C0 17.0867 4.91333 22 11 22C17.0867 22 22 17.0867 22 11C22 4.91333 17.0867 0 11 0ZM16.5978 14.7156C17.1111 15.2289 17.1111 16.06 16.5978 16.5733C16.3289 16.8422 16.0111 16.9644 15.6689 16.9644C15.3267 16.9644 14.9844 16.8422 14.74 16.5733L11.0733 12.9067L7.38222 16.5733C7.11333 16.8422 6.79556 16.9644 6.45333 16.9644C6.11111 16.9644 5.76889 16.8422 5.52444 16.5733C5.01111 16.06 5.01111 15.2289 5.52444 14.7156L9.21556 11.0244L5.52444 7.35778C5.01111 6.84444 5.01111 6.01333 5.52444 5.5C6.03778 4.98667 6.86889 4.98667 7.38222 5.5L11.0733 9.19111L14.7644 5.5C15.2778 4.98667 16.1089 4.98667 16.6222 5.5C17.1356 6.01333 17.1356 6.84444 16.6222 7.35778L12.9311 11.0489L16.5978 14.7156Z"
                    />
                  </svg>
                </span>
              </template>
            </VueDatePicker>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between mb-4">
        <div class="flex">
          <div class="relative">
            <span v-if="scheduleTo" class="datepicker-title-span"
              >Schedule To (Optional)</span
            >
            <VueDatePicker
              id="schedule-to-picker"
              auto-apply
              :clearable="scheduleTo ? true : false"
              format="dd/MM/yyyy"
              v-model="scheduleTo"
              :month-change-on-scroll="false"
              type="date"
              :disabled-dates="disabledScheduleToDates"
              :disabled="!scheduleFrom"
              :class="{ 'disabled-div': !scheduleFrom }"
              mode="date"
              :enable-time-picker="false"
            >
              <template #dp-input="{ value }">
                <input
                  placeholder="Schedule To (Optional)"
                  type="text"
                  :value="value"
                  class="custom-date-picker"
                  :class="scheduleTo ? 'pt-15' : ''"
                />
              </template>

              <template v-if="!scheduleTo" #input-icon>
                <i class="input-slot-image">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      :fill="scheduleTo ? '#2D8425' : '#787878'"
                      d="M0.666626 14.8327C0.666626 16.2493 1.74996 17.3327 3.16663 17.3327H14.8333C16.25 17.3327 17.3333 16.2493 17.3333 14.8327V8.16602H0.666626V14.8327ZM14.8333 2.33268H13.1666V1.49935C13.1666 0.999349 12.8333 0.666016 12.3333 0.666016C11.8333 0.666016 11.5 0.999349 11.5 1.49935V2.33268H6.49996V1.49935C6.49996 0.999349 6.16663 0.666016 5.66663 0.666016C5.16663 0.666016 4.83329 0.999349 4.83329 1.49935V2.33268H3.16663C1.74996 2.33268 0.666626 3.41602 0.666626 4.83268V6.49935H17.3333V4.83268C17.3333 3.41602 16.25 2.33268 14.8333 2.33268Z"
                    />
                  </svg>
                </i>
              </template>
              <template v-if="scheduleTo" #clear-icon="{ clear }">
                <span @click="clear">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      fill="#F46A6A"
                      d="M11 0C4.91333 0 0 4.91333 0 11C0 17.0867 4.91333 22 11 22C17.0867 22 22 17.0867 22 11C22 4.91333 17.0867 0 11 0ZM16.5978 14.7156C17.1111 15.2289 17.1111 16.06 16.5978 16.5733C16.3289 16.8422 16.0111 16.9644 15.6689 16.9644C15.3267 16.9644 14.9844 16.8422 14.74 16.5733L11.0733 12.9067L7.38222 16.5733C7.11333 16.8422 6.79556 16.9644 6.45333 16.9644C6.11111 16.9644 5.76889 16.8422 5.52444 16.5733C5.01111 16.06 5.01111 15.2289 5.52444 14.7156L9.21556 11.0244L5.52444 7.35778C5.01111 6.84444 5.01111 6.01333 5.52444 5.5C6.03778 4.98667 6.86889 4.98667 7.38222 5.5L11.0733 9.19111L14.7644 5.5C15.2778 4.98667 16.1089 4.98667 16.6222 5.5C17.1356 6.01333 17.1356 6.84444 16.6222 7.35778L12.9311 11.0489L16.5978 14.7156Z"
                    />
                  </svg>
                </span>
              </template>
            </VueDatePicker>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between mb-3">
        <div class="flex">
          <div class="relative">
            <span v-if="startTime" class="datepicker-title-span"
              >Start Time (Optional)</span
            >
            <VueDatePicker
              id="start-time-picker"
              auto-apply
              :clearable="startTime ? true : false"
              format="HH:mm"
              v-model="startTime"
              :month-change-on-scroll="false"
              type="time"
              time-picker
            >
              <template #dp-input="{ value }">
                <input
                  placeholder="Start Time (Optional)"
                  type="text"
                  :value="value"
                  class="custom-date-picker"
                  :class="startTime ? 'pt-15' : ''"
                />
              </template>

              <template v-if="!startTime" #input-icon>
                <i class="input-slot-image">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      :fill="startTime ? '#2D8425' : '#787878'"
                      d="M0.666626 14.8327C0.666626 16.2493 1.74996 17.3327 3.16663 17.3327H14.8333C16.25 17.3327 17.3333 16.2493 17.3333 14.8327V8.16602H0.666626V14.8327ZM14.8333 2.33268H13.1666V1.49935C13.1666 0.999349 12.8333 0.666016 12.3333 0.666016C11.8333 0.666016 11.5 0.999349 11.5 1.49935V2.33268H6.49996V1.49935C6.49996 0.999349 6.16663 0.666016 5.66663 0.666016C5.16663 0.666016 4.83329 0.999349 4.83329 1.49935V2.33268H3.16663C1.74996 2.33268 0.666626 3.41602 0.666626 4.83268V6.49935H17.3333V4.83268C17.3333 3.41602 16.25 2.33268 14.8333 2.33268Z"
                    />
                  </svg>
                </i>
              </template>
              <template v-if="startTime" #clear-icon="{ clear }">
                <span @click="clear">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      fill="#F46A6A"
                      d="M11 0C4.91333 0 0 4.91333 0 11C0 17.0867 4.91333 22 11 22C17.0867 22 22 17.0867 22 11C22 4.91333 17.0867 0 11 0ZM16.5978 14.7156C17.1111 15.2289 17.1111 16.06 16.5978 16.5733C16.3289 16.8422 16.0111 16.9644 15.6689 16.9644C15.3267 16.9644 14.9844 16.8422 14.74 16.5733L11.0733 12.9067L7.38222 16.5733C7.11333 16.8422 6.79556 16.9644 6.45333 16.9644C6.11111 16.9644 5.76889 16.8422 5.52444 16.5733C5.01111 16.06 5.01111 15.2289 5.52444 14.7156L9.21556 11.0244L5.52444 7.35778C5.01111 6.84444 5.01111 6.01333 5.52444 5.5C6.03778 4.98667 6.86889 4.98667 7.38222 5.5L11.0733 9.19111L14.7644 5.5C15.2778 4.98667 16.1089 4.98667 16.6222 5.5C17.1356 6.01333 17.1356 6.84444 16.6222 7.35778L12.9311 11.0489L16.5978 14.7156Z"
                    />
                  </svg>
                </span>
              </template>
            </VueDatePicker>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between mb-3">
        <div class="flex">
          <div class="relative">
            <span v-if="endTime" class="datepicker-title-span"
              >End Time (Optional)</span
            >
            <VueDatePicker
              id="end-time-picker"
              auto-apply
              :clearable="endTime ? true : false"
              format="HH:mm"
              v-model="endTime"
              :month-change-on-scroll="false"
              type="time"
              time-picker
            >
              <template #dp-input="{ value }">
                <input
                  placeholder="End Time (Optional)"
                  type="text"
                  :value="value"
                  class="custom-date-picker"
                  :class="endTime ? 'pt-15' : ''"
                />
              </template>

              <template v-if="!endTime" #input-icon>
                <i class="input-slot-image">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      :fill="endTime ? '#2D8425' : '#787878'"
                      d="M0.666626 14.8327C0.666626 16.2493 1.74996 17.3327 3.16663 17.3327H14.8333C16.25 17.3327 17.3333 16.2493 17.3333 14.8327V8.16602H0.666626V14.8327ZM14.8333 2.33268H13.1666V1.49935C13.1666 0.999349 12.8333 0.666016 12.3333 0.666016C11.8333 0.666016 11.5 0.999349 11.5 1.49935V2.33268H6.49996V1.49935C6.49996 0.999349 6.16663 0.666016 5.66663 0.666016C5.16663 0.666016 4.83329 0.999349 4.83329 1.49935V2.33268H3.16663C1.74996 2.33268 0.666626 3.41602 0.666626 4.83268V6.49935H17.3333V4.83268C17.3333 3.41602 16.25 2.33268 14.8333 2.33268Z"
                    />
                  </svg>
                </i>
              </template>
              <template v-if="endTime" #clear-icon="{ clear }">
                <span @click="clear">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      fill="#F46A6A"
                      d="M11 0C4.91333 0 0 4.91333 0 11C0 17.0867 4.91333 22 11 22C17.0867 22 22 17.0867 22 11C22 4.91333 17.0867 0 11 0ZM16.5978 14.7156C17.1111 15.2289 17.1111 16.06 16.5978 16.5733C16.3289 16.8422 16.0111 16.9644 15.6689 16.9644C15.3267 16.9644 14.9844 16.8422 14.74 16.5733L11.0733 12.9067L7.38222 16.5733C7.11333 16.8422 6.79556 16.9644 6.45333 16.9644C6.11111 16.9644 5.76889 16.8422 5.52444 16.5733C5.01111 16.06 5.01111 15.2289 5.52444 14.7156L9.21556 11.0244L5.52444 7.35778C5.01111 6.84444 5.01111 6.01333 5.52444 5.5C6.03778 4.98667 6.86889 4.98667 7.38222 5.5L11.0733 9.19111L14.7644 5.5C15.2778 4.98667 16.1089 4.98667 16.6222 5.5C17.1356 6.01333 17.1356 6.84444 16.6222 7.35778L12.9311 11.0489L16.5978 14.7156Z"
                    />
                  </svg>
                </span>
              </template>
            </VueDatePicker>
          </div>
        </div>
      </div>

      <div class="relative mb-3">
        <span v-if="scheduleType" class="multiselect-title-span"
          >Schedule Type</span
        >
        <VMultiselect
          v-model="scheduleType"
          :options="scheduleTypeOptions"
          :disabled="!scheduleTypeOptions.length"
          :loading="!scheduleTypeOptions.length"
          :searchable="true"
          :close-on-select="true"
          :show-labels="false"
          open-direction="bottom"
          placeholder="Schedule Type"
          trackBy="name"
          label="description"
          @select="onScheduleTypeChange"
        />
      </div>

      <div
        v-if="scheduleType && scheduleType.name === 'weekly'"
        class="relative mb-3"
      >
        <span v-if="daysOfWeek.length" class="multiselect-title-span"
          >Days of Week</span
        >
        <VMultiselect
          :class="{ 'multiselect-options': daysOfWeek.length }"
          v-model="daysOfWeek"
          :options="daysOfWeekOptions"
          :disabled="!daysOfWeekOptions.length"
          :loading="!daysOfWeekOptions.length"
          :searchable="true"
          :close-on-select="true"
          :show-labels="false"
          open-direction="bottom"
          placeholder="Days of Week"
          trackBy="id"
          label="name"
          :multiple="true"
        />
      </div>

      <div
        v-if="scheduleType && scheduleType.name === 'monthly'"
        class="relative mb-3"
      >
        <span v-if="daysOfMonth.length" class="multiselect-title-span"
          >Days of Month</span
        >
        <VMultiselect
          :class="{ 'multiselect-options': daysOfMonth.length }"
          v-model="daysOfMonth"
          :options="daysOfMonthOptions"
          :disabled="!daysOfMonthOptions.length"
          :multiple="true"
          :searchable="true"
          :close-on-select="true"
          :show-labels="false"
          open-direction="bottom"
          placeholder="Days of Month"
          trackBy="id"
          label="name"
        />
      </div>

      <div
        v-if="scheduleType && scheduleType.name === 'yearly'"
        class="relative mb-3"
      >
        <span v-if="monthsOfYear.length" class="multiselect-title-span"
          >Months of Year</span
        >
        <VMultiselect
          :class="{ 'multiselect-options': monthsOfYear.length }"
          v-model="monthsOfYear"
          :options="monthsOfYearOptions"
          :disabled="!monthsOfYearOptions.length"
          :loading="!monthsOfYearOptions.length"
          :searchable="true"
          :close-on-select="true"
          :show-labels="false"
          open-direction="bottom"
          placeholder="Months of Year"
          trackBy="id"
          label="name"
          :multiple="true"
        />
      </div>
    </template>
    <template #footer>
      <div class="w-full">
        <div class="seperator text-center"></div>
        <div class="flex mt-20 justify-center">
          <button
            :disabled="
              (!stores.length && !areas.length && !devices.length) ||
              !scheduleType ||
              (scheduleType &&
                scheduleType.name === 'weekly' &&
                !daysOfWeek.length) ||
              (scheduleType &&
                scheduleType.name === 'monthly' &&
                !daysOfMonth.length) ||
              (scheduleType &&
                scheduleType.name === 'yearly' &&
                !monthsOfYear.length) ||
              !scheduleFrom ||
              isSubmitted
            "
            @click="onSubmit"
            type="button"
            class="btn btn-primary add-button mr-30"
          >
            Add
            <span
              v-if="isSubmitted"
              class="spinner-sm"
              role="status"
              aria-hidden="true"
            ></span>
          </button>
          <button
            @click="resetStoreData"
            type="button"
            class="btn btn-secondary cancel-button"

          >
            Cancel
          </button>
        </div>
      </div>
    </template>
  </DModal>
</template>
